/**
 * The sign-in wall for the `frappe` auth provider (Akhil, 2026-09-24): an instance served by the
 * Frappe site itself, with Clerk switched off. A Frappe username and password, posted to the
 * site's own `/api/method/login`, which sets the `sid` cookie every later call carries.
 *
 * Nothing is remembered here. No token in memory, nothing in browser storage (barred), no
 * "signed in" flag: the cookie IS the session, and `FrappeLoginGate` asks the server who it is
 * rather than keeping an opinion of its own. `onSignedIn` only tells the gate to ask again.
 *
 * The password field is `type="password"` and the value never leaves this component except in
 * the login request — it is not logged, not put in a query key, and not echoed in an error.
 */
import { useState, type FormEvent } from 'react'
import { SESSION_NOT_KEPT, sessionIsLive, signInWithPassword } from '../lib/api/frappeLogin'

export function FrappeLoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || user.trim() === '' || password === '') return
    setBusy(true)
    setError(null)
    const result = await signInWithPassword(user, password)
    if (!result.ok) {
      setBusy(false)
      setError(result.reason)
      // The password stays typed: a wrong USERNAME is the likelier mistake, and clearing it
      // makes the person retype something they got right.
      return
    }
    /*
      A 200 is not yet a session. Frappe's `sid` is `Secure`, and a browser that drops it over
      plain http leaves the login succeeding with nothing kept — the gate then asks who is signed
      in, hears "nobody", and puts this page straight back up. Asking the site ourselves turns
      that silent loop into a sentence that says what to do (2026-09-24).
    */
    const live = await sessionIsLive()
    setBusy(false)
    if (!live) {
      setError(SESSION_NOT_KEPT)
      return
    }
    setPassword('')
    onSignedIn()
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <form
        onSubmit={submit}
        className="card"
        style={{ width: 360, padding: 24, background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12 }}
      >
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 2 }}>Sign in to Bosun</div>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 16 }}>
          Your ERP username and password — the same ones you use for the ERP itself.
        </div>

        <label className="fld" style={{ display: 'block', marginBottom: 10 }}>
          <span>Username or email</span>
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            autoFocus
            disabled={busy}
            style={{ width: '100%' }}
          />
        </label>

        <label className="fld" style={{ display: 'block', marginBottom: 14 }}>
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
            style={{ width: '100%' }}
          />
        </label>

        {error ? (
          <div role="alert" style={{ color: 'var(--danger)', fontSize: 12.5, marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          className="btn primary"
          disabled={busy || user.trim() === '' || password === ''}
          style={{ width: '100%' }}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
